import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, Calendar, FileText } from "lucide-react";
import { format } from "date-fns";
import { proxyGoogleImage } from "../lib/googleImageProxy";

interface Post {
  post_id: string;
  google_post_id: string;
  status: string;
  published_at: string;
  metadata: any;
  post_content: {
    id: string;
    summary: string;
    media_urls?: string[];
    type?: string;
    call_to_action?: any;
    event_info?: any;
    offer_info?: any;
  };
}

interface StoreLocatorPostsSectionProps {
  locationId: string;
  theme: any;
  onPostClick: (post: Post) => void;
  ssrPostsData?: Post[]; // Pre-fetched posts from SSR
}

const StoreLocatorPostsSection = ({
  locationId,
  theme,
  onPostClick,
  ssrPostsData,
}: StoreLocatorPostsSectionProps) => {
  const [posts, setPosts] = useState<Post[]>(ssrPostsData || []);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // If SSR data is provided, use it
    if (ssrPostsData && ssrPostsData.length > 0) {
      console.log('[StoreLocatorPostsSection] Using SSR posts data');
      setPosts(ssrPostsData);
      return;
    }

    // Otherwise fetch from database
    if (locationId) {
      fetchLocationPosts();
    }
  }, [locationId, ssrPostsData]);

  const formatDate = (dateString: string) => {
    // Use date-fns rather than toLocaleDateString: the latter is
    // locale/timezone-dependent, which produces different strings on
    // the SSR server (UTC, en-US) vs the client browser (visitor's
    // locale + timezone) and triggers React hydration mismatches on
    // this microsite (Sentry: microsite-hydration-mismatch).
    return format(new Date(dateString), 'dd MMM yy');
  };

  const fetchLocationPosts = async () => {
    try {
      setLoading(true);
      console.log('Fetching posts for location:', locationId);

      // Fetch post locations
      const { data: postLocations, error: locationsError } = await supabase
        .from("gmb_post_locations")
        .select("*")
        .eq("location_id", locationId)
        .eq("status", "published")
        .order("published_at", { ascending: false })
        .limit(6);

      if (locationsError) {
        console.error("Error fetching post locations:", locationsError);
        setPosts([]);
        return;
      }

      if (postLocations && postLocations.length > 0) {
        const postIds = postLocations.map(p => p.post_id);

        const { data: postContent, error: contentError } = await supabase
          .from("gmb_post_content")
          .select("*")
          .in("id", postIds);

        if (contentError) {
          console.error("Error fetching post content:", contentError);
          // Use metadata fallback
          const postsWithMetadata = postLocations.map(loc => ({
            ...loc,
            post_content: {
              id: loc.post_id,
              summary: loc.metadata?.summary || loc.metadata?.text || "",
              media_urls: loc.metadata?.media_urls || loc.metadata?.mediaUrls || [],
              type: loc.metadata?.type || loc.metadata?.postType || "STANDARD",
              call_to_action: loc.metadata?.call_to_action || loc.metadata?.callToAction,
              event_info: loc.metadata?.event_info || loc.metadata?.eventInfo,
              offer_info: loc.metadata?.offer_info || loc.metadata?.offerInfo
            }
          }));

          const postsWithContent = postsWithMetadata.filter(post =>
            post.post_content?.summary && post.post_content.summary.trim().length > 0
          );

          setPosts(postsWithContent);
          return;
        }

        // Merge the data
        const mergedPosts = postLocations.map(loc => {
          const content = postContent?.find(c => c.id === loc.post_id);
          return {
            ...loc,
            post_content: content || {
              id: loc.post_id,
              summary: loc.metadata?.summary || loc.metadata?.text || "",
              media_urls: loc.metadata?.media_urls || loc.metadata?.mediaUrls || [],
              type: loc.metadata?.type || loc.metadata?.postType || "STANDARD",
              call_to_action: loc.metadata?.call_to_action || loc.metadata?.callToAction,
              event_info: loc.metadata?.event_info || loc.metadata?.eventInfo,
              offer_info: loc.metadata?.offer_info || loc.metadata?.offerInfo
            }
          };
        });

        const postsWithContent = mergedPosts.filter(post =>
          post.post_content?.summary && post.post_content.summary.trim().length > 0
        );

        setPosts(postsWithContent);
      } else {
        setPosts([]);
      }
    } catch (error) {
      console.error("Error fetching posts:", error);
      setPosts([]);
    } finally {
      setLoading(false);
    }
  };

  const getImageUrl = (mediaUrls: string[] | undefined) => {
    if (!mediaUrls || !Array.isArray(mediaUrls) || mediaUrls.length === 0) {
      return null;
    }
    const firstUrl = mediaUrls[0];
    if (typeof firstUrl === 'string' && firstUrl.length > 0) {
      return firstUrl;
    }
    return null;
  };

  if (loading) {
    return (
      <div>
        <p className="font-medium mb-3">Latest Posts</p>
        <div className="flex justify-center py-4">
          <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
        </div>
      </div>
    );
  }

  if (!posts || posts.length === 0) {
    return null;
  }

  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <FileText className="h-4 w-4 md:h-5 md:w-5 text-gray-400" />
        <h3 className="font-semibold text-base md:text-lg">Latest Posts</h3>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {posts.map((post) => {
          const imageUrl = getImageUrl(post.post_content?.media_urls);

          return (
            <div
              key={post.post_id}
              onClick={() => onPostClick(post)}
              className="bg-white border rounded-lg p-3 cursor-pointer hover:shadow-md transition-shadow"
            >
              {imageUrl && (
                <div className="w-full h-24 md:h-32 overflow-hidden rounded mb-2 bg-gray-100">
                  {/* Post summary is rendered directly below, so the artwork is
                      decorative and takes a deliberate empty alt. */}
                  <img
                    src={proxyGoogleImage(imageUrl, 400)}
                    alt=""
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      // Fires only when the (already proxied) URL genuinely fails.
                      const container = e.currentTarget.parentElement;
                      if (container) container.style.display = 'none';
                    }}
                  />
                </div>
              )}
              <div className="space-y-2">
                {post.post_content?.summary && (
                  <p className="text-xs md:text-sm text-gray-800 overflow-hidden" style={{ display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical' }}>
                    {post.post_content.summary}
                  </p>
                )}
                <div className="flex items-center gap-1 text-xs text-gray-500">
                  <Calendar className="h-3 w-3" />
                  {formatDate(post.published_at)}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default StoreLocatorPostsSection;
